// ==================== GURUKUL PAGE (Add User + My Family) ====================
// Reached both from OK Admin's Gurukul tab (managers/coordinators) and
// directly at /gurukul (anyone holding a Gurukul role — see main.js
// renderGurukulPage / okAccess.js hasAppAccess's gurukul special-case).
import { state } from '../state.js';
import { showToast, showConfirm } from '../components/toasts.js';
import { teamsSectionHtml, initTeamsSection } from './gurukul-teams.js';
import { retireSectionHtml, initRetireSection } from './gurukul-retire.js';
import { auditSectionHtml, initAuditSection } from './gurukul-audit.js';

const ROLE_OPTIONS = [
  { code: 'parent', label: 'Parent' },
  { code: 'learner', label: 'Learner (Adult Student)' },
  { code: 'coordinator', label: 'Coordinator' },
  { code: 'tester', label: 'Tester' },
  { code: 'tech_team', label: 'Tech Team' },
  { code: 'lms_team', label: 'LMS Team' },
  { code: 'gurukul_admin', label: 'Gurukul Admin' },
];
const COORDINATOR_ROLES = new Set(['parent', 'learner']);

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');
}

// The Gurukul world tells us the caller's real roles (decided by the server); fall back to local state.
let roleFlags = null;
export function setGurukulRoleFlags(flags) { roleFlags = flags; }

function isGurukulManager() {
  if (roleFlags) return !!roleFlags.manager;
  return !!state.isOkAdmin || (state.okAppAdmins || []).includes('gurukul');
}

function isGurukulCoordinator() {
  if (roleFlags) return !!roleFlags.coordinator;
  return (state.appRoleAssignments || []).some(a => a.app_code === 'gurukul' && a.role_code === 'coordinator');
}

function isGurukulParent() {
  if (roleFlags) return !!roleFlags.parent;
  return (state.appRoleAssignments || []).some(a => a.app_code === 'gurukul' && a.role_code === 'parent');
}

async function apiCall(path, options = {}) {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ──────────────────────────── Add User (manager/coordinator) ────────────────

function addUserSectionHtml(manager) {
  const allowedRoles = manager ? ROLE_OPTIONS : ROLE_OPTIONS.filter(r => COORDINATOR_ROLES.has(r.code));
  return `
    <style>
      .gk-compact .form-group input[type="text"], .gk-compact .form-group input[type="email"],
      .gk-compact .form-group input[type="password"], .gk-compact .form-group input[type="number"],
      .gk-compact .form-group select { max-width: 300px; width: 100%; }
      .gk-compact .form-group textarea { max-width: 620px; width: 100%; }
      .gk-compact .gk-inline { display: flex; gap: 8px; align-items: center; max-width: 300px; }
      .gk-compact .gk-inline input { flex: 1; min-width: 0; }
      .gk-compact .gk-choices { display: flex; flex-wrap: wrap; gap: 6px 18px; }
    </style>
    <div class="card gk-compact" id="gk-sec-add-user">
      <h2>Add User</h2>
      <p class="page-intro">${manager
      ? 'Accounts are created immediately.'
      : 'Your submission goes to a Gurukul admin, who approves, rejects or asks you for clarification. Track it under Requests.'}</p>
      <form id="gkAddUserForm" class="form-stack" autocomplete="off">
        <div class="form-grid-row">
          <div class="form-group"><label for="gkFirstName">First name</label><input type="text" id="gkFirstName" required maxlength="60"></div>
          <div class="form-group"><label for="gkLastName">Last name</label><input type="text" id="gkLastName" required maxlength="60"></div>
          <div class="form-group"><label for="gkEmail">Email</label><input type="email" id="gkEmail" required maxlength="254"></div>
        </div>
        <div class="form-grid-row">
          <div class="form-group">
            <label for="gkTempPassword">Temp password (8–72 characters)</label>
            <div class="gk-inline">
              <input type="password" id="gkTempPassword" required minlength="8" maxlength="72" autocomplete="new-password">
              <button type="button" id="gkTogglePwBtn" class="secondary" style="white-space:nowrap;">Show</button>
            </div>
          </div>
          <div class="form-group">
            <label for="gkTempPasswordConfirm">Confirm password</label>
            <input type="password" id="gkTempPasswordConfirm" required minlength="8" maxlength="72" autocomplete="new-password">
          </div>
        </div>
        <div class="form-grid-row">
          <div class="form-group">
            <label>Gender</label>
            <div class="gk-choices">
              <label style="display:flex;align-items:center;gap:6px;font-weight:normal;"><input type="radio" name="gkGender" value="female" checked> Female</label>
              <label style="display:flex;align-items:center;gap:6px;font-weight:normal;"><input type="radio" name="gkGender" value="male"> Male</label>
            </div>
          </div>
          <div class="form-group">
            <label>Messaging</label>
            <label style="display:flex;align-items:center;gap:6px;font-weight:normal;"><input type="checkbox" id="gkAllowOpposite" checked> Allow cross-gender messaging (untick for monastics)</label>
          </div>
        </div>
        <div class="form-group">
          <label>Roles</label>
          <div id="gkRoleChecks" class="gk-choices">
            ${allowedRoles.map(r => `
              <label style="display:flex;align-items:center;gap:6px;font-weight:normal;">
                <input type="checkbox" class="gk-role-check" value="${r.code}"> ${escapeHtml(r.label)}
              </label>`).join('')}
          </div>
        </div>
        <div class="form-grid-row">
          <div class="form-group" id="gkTeamGroup" style="display:none;">
            <label for="gkTeam">Team</label>
            <select id="gkTeam"></select>
            <p id="gkTeamNote" class="page-intro" style="margin:4px 0 0;"></p>
          </div>
          <div class="form-group" id="gkChildrenGroup" style="display:none;">
            <label for="gkNumChildren">Number of children</label>
            <input type="number" id="gkNumChildren" min="0" max="20" value="1">
          </div>
        </div>
        <div class="form-group" id="gkChildPathsGroup" style="display:none;">
          <label for="gkProgram">Program</label>
          <select id="gkProgram"></select>
          <p class="page-intro" style="margin:4px 0 8px;">Tick the Learning Paths for each child. Every child needs at least one.</p>
          <div id="gkChildPaths"></div>
        </div>
        <div class="form-group">
          <label for="gkApproverNotes">Notes for the approver (optional)</label>
          <textarea id="gkApproverNotes" rows="2" maxlength="1000" placeholder="Anything the approver should know about this person"></textarea>
        </div>
        <button type="submit" id="gkAddUserBtn">${manager ? 'Create user' : 'Submit for approval'}</button>
        <p id="gkSubmitResult" style="display:none;margin-top:8px;font-weight:600;"></p>
      </form>
    </div>
  `;
}

let addUserTeams = [];
let addUserPrograms = [];

/** LMS-04: one row per child (Child 1..N) with the chosen Program's Learning Paths as tick boxes + "All". */
function renderChildPaths() {
  const group = document.getElementById('gkChildPathsGroup');
  const box = document.getElementById('gkChildPaths');
  if (!group || !box) return;
  const isParent = [...document.querySelectorAll('.gk-role-check:checked')].some(cb => cb.value === 'parent');
  const n = Math.max(0, Math.min(20, parseInt(document.getElementById('gkNumChildren')?.value, 10) || 0));
  group.style.display = isParent && n > 0 ? '' : 'none';
  if (!isParent || !n) { box.innerHTML = ''; updateAddUserSubmit(); return; }
  const progSel = document.getElementById('gkProgram');
  if (!progSel.options.length) {
    progSel.innerHTML = addUserPrograms.length
      ? addUserPrograms.map(p => `<option value="${p.id}">${escapeHtml(p.name)}</option>`).join('')
      : '<option value="">No Program with Learning Paths yet</option>';
  }
  const program = addUserPrograms.find(p => p.id === progSel.value);
  const previous = collectChildPaths();
  box.innerHTML = program ? Array.from({ length: n }, (_, i) => {
    const prev = previous[i] || { path_ids: [], scope: 'entire', months: [] };
    return `<div class="gk-child-row" data-slot="${i}" style="border:1px solid var(--border);border-radius:8px;padding:8px 12px;margin-bottom:8px;max-width:760px;">
      <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <strong>Child ${i + 1}</strong>
        <label style="display:flex;align-items:center;gap:6px;font-weight:normal;"><input type="checkbox" class="gk-cp-all"> All</label>
      </div>
      <div class="gk-choices" style="margin:6px 0;">
        ${program.paths.map(p => `<label style="display:flex;align-items:center;gap:6px;font-weight:normal;">
          <input type="checkbox" class="gk-cp-path" value="${p.id}"${prev.path_ids.includes(p.id) ? ' checked' : ''}> ${escapeHtml(p.name)}</label>`).join('')}
      </div>
      <div style="display:flex;gap:14px;flex-wrap:wrap;align-items:center;">
        <label style="display:flex;align-items:center;gap:6px;font-weight:normal;"><input type="radio" name="gkScope${i}" value="entire"${prev.scope !== 'months' ? ' checked' : ''}> Entire path</label>
        <label style="display:flex;align-items:center;gap:6px;font-weight:normal;"><input type="radio" name="gkScope${i}" value="months"${prev.scope === 'months' ? ' checked' : ''}> Only month(s):</label>
        <input type="text" class="gk-cp-months" placeholder="e.g. 1 or 1,2" maxlength="40" style="max-width:140px;" value="${escapeHtml((prev.months || []).join(','))}">
      </div>
    </div>`;
  }).join('') : '<p class="page-intro">Create a Program with Learning Paths first (Admin › Programs &amp; courses).</p>';
  box.querySelectorAll('.gk-child-row').forEach(row => {
    const all = row.querySelector('.gk-cp-all');
    const boxes = [...row.querySelectorAll('.gk-cp-path')];
    const sync = () => { all.checked = boxes.length > 0 && boxes.every(b => b.checked); };
    sync();
    all.addEventListener('change', () => { boxes.forEach(b => { b.checked = all.checked; }); updateAddUserSubmit(); });
    boxes.forEach(b => b.addEventListener('change', () => { sync(); updateAddUserSubmit(); }));
  });
  updateAddUserSubmit();
}

/** Submit stays disabled until every child has at least one Learning Path (parent requests only). */
function updateAddUserSubmit() {
  const btn = document.getElementById('gkAddUserBtn');
  if (!btn) return;
  const visible = document.getElementById('gkChildPathsGroup')?.style.display !== 'none';
  const rows = [...document.querySelectorAll('#gkChildPaths .gk-child-row')];
  const ok = !visible || (rows.length > 0 && rows.every(r => r.querySelector('.gk-cp-path:checked')));
  btn.disabled = !ok;
  btn.title = ok ? '' : 'Choose at least one Learning Path for every child';
}

function collectChildPaths() {
  return [...document.querySelectorAll('#gkChildPaths .gk-child-row')].map((row, i) => ({
    path_ids: [...row.querySelectorAll('.gk-cp-path:checked')].map(b => b.value),
    scope: row.querySelector(`input[name="gkScope${i}"]:checked`)?.value === 'months' ? 'months' : 'entire',
    months: (row.querySelector('.gk-cp-months')?.value || '').split(/[\s,]+/).filter(Boolean).map(Number),
  }));
}

function refreshTeamOptions() {
  const roles = [...document.querySelectorAll('.gk-role-check:checked')].map(cb => cb.value);
  const kids = document.getElementById('gkChildrenGroup');
  if (kids) kids.style.display = roles.includes('parent') ? '' : 'none';
  renderChildPaths();
  const needsTeam = roles.some(r => ['parent', 'learner', 'coordinator'].includes(r));
  const familyRole = roles.some(r => ['parent', 'learner'].includes(r));
  const group = document.getElementById('gkTeamGroup');
  const select = document.getElementById('gkTeam');
  const note = document.getElementById('gkTeamNote');
  if (!group || !select) return;
  group.style.display = needsTeam ? '' : 'none';
  if (!needsTeam) return;
  const previous = select.value;
  const options = addUserTeams.filter(t => !(familyRole && t.is_system));
  select.innerHTML = options.length
    ? options.map(t => '<option value="' + t.id + '">' + escapeHtml(t.name) + '</option>').join('')
    : '<option value="">No team available</option>';
  const unallocated = options.find(t => t.is_system);
  if (previous && options.some(t => t.id === previous)) select.value = previous;
  else if (!familyRole && unallocated) select.value = unallocated.id;
  if (note) {
    note.textContent = options.length
      ? (familyRole ? 'Parents and learners must be placed in a real team.' : 'Coordinators default to Unallocated until an admin places them.')
      : 'You are not placed in a team yet, so you cannot add people. Ask an admin to allocate you.';
  }
}

async function loadAddUserOptions() {
  try {
    const { teams, programs } = await apiCall('/api/gurukul/add-user-options');
    addUserTeams = teams || [];
    addUserPrograms = programs || [];
  } catch (err) {
    addUserTeams = [];
    addUserPrograms = [];
  }
  refreshTeamOptions();
}

function initAddUserSection() {
  document.querySelectorAll('.gk-role-check').forEach(cb => cb.addEventListener('change', refreshTeamOptions));
  document.getElementById('gkNumChildren')?.addEventListener('input', renderChildPaths);
  document.getElementById('gkProgram')?.addEventListener('change', () => { document.getElementById('gkChildPaths').innerHTML = ''; renderChildPaths(); });
  loadAddUserOptions();

  const form = document.getElementById('gkAddUserForm');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const roles = [...document.querySelectorAll('.gk-role-check:checked')].map(cb => cb.value);
      if (!roles.length) {
        showToast('Select at least one role', 'error');
        return;
      }
      const password = document.getElementById('gkTempPassword').value;
      const confirm = document.getElementById('gkTempPasswordConfirm').value;
      if (password !== confirm) {
        showToast('Temp password and confirmation do not match', 'error');
        return;
      }
      const email = document.getElementById('gkEmail').value.trim();
      const needsTeam = roles.some(r => ['parent', 'learner', 'coordinator'].includes(r));
      const teamId = needsTeam ? document.getElementById('gkTeam').value : null;
      if (needsTeam && !teamId) {
        showToast('Select a team', 'error');
        return;
      }
      const payload = {
        first_name: document.getElementById('gkFirstName').value.trim(),
        last_name: document.getElementById('gkLastName').value.trim(),
        email,
        temp_password: password,
        roles,
        gender: document.querySelector('input[name="gkGender"]:checked')?.value || 'female',
        allow_opposite_gender: !!document.getElementById('gkAllowOpposite')?.checked,
        team_id: teamId,
        approver_notes: document.getElementById('gkApproverNotes').value.trim(),
      };
      if (roles.includes('parent')) {
        payload.num_children = Number(document.getElementById('gkNumChildren').value);
        if (payload.num_children > 0) {
          const kids = collectChildPaths();
          const missing = kids.findIndex(k => !k.path_ids.length);
          if (!kids.length || missing >= 0) { showToast(`Child ${missing >= 0 ? missing + 1 : 1}: choose at least one Learning Path`, 'error'); return; }
          const badMonths = kids.findIndex(k => k.scope === 'months' && (!k.months.length || k.months.some(m => !Number.isInteger(m) || m < 1)));
          if (badMonths >= 0) { showToast(`Child ${badMonths + 1}: enter the month numbers, e.g. 1 or 1,2`, 'error'); return; }
          payload.program_id = document.getElementById('gkProgram').value;
          payload.child_paths = kids;
        }
      }
      const btn = document.getElementById('gkAddUserBtn');
      const resultDiv = document.getElementById('gkSubmitResult');
      btn.disabled = true;
      try {
        const result = await apiCall('/api/gurukul/user-requests', { method: 'POST', body: JSON.stringify(payload) });
        const message = result.status === 'approved'
          ? `✅ Gurukul access granted for ${email}. If this is a brand-new account, they must change the temp password on first login — if the email already had an account, their existing password still applies.`
          : `✅ Submitted for approval as ${result.requestNo || 'a request'}: ${email}. Track it under Requests.`;
        showToast(message, 'success');
        if (resultDiv) { resultDiv.textContent = message; resultDiv.style.display = 'block'; }
        form.reset();
        refreshTeamOptions();
      } catch (err) {
        showToast(err.message || 'Submit failed', 'error');
      } finally {
        btn.disabled = false;
        updateAddUserSubmit();
      }
    });
  }

  document.getElementById('gkTogglePwBtn')?.addEventListener('click', () => {
    const input = document.getElementById('gkTempPassword');
    const btn = document.getElementById('gkTogglePwBtn');
    const showing = input.type === 'text';
    input.type = showing ? 'password' : 'text';
    btn.textContent = showing ? 'Show' : 'Hide';
  });

}

// ──────────────────────────── My Family (parent) ────────────────────────────

function myFamilySectionHtml() {
  return `
    <div class="card" id="gk-sec-family-enroll">
      <h2>Enroll a Learner</h2>
      <p class="page-intro">Enrolling now happens in guided steps — read the policies, tick the acknowledgments and sign.
      Use <strong>Enroll a learner</strong> in the menu on the left.</p>
    </div>

    <div class="card" id="gk-sec-family">
      <h2>My Family</h2>
      <div id="gkFamilyList"><p class="empty-state">Loading…</p></div>
    </div>

    <div class="card" id="gk-sec-family-guardian">
      <h2>Add a Guardian</h2>
      <p class="page-intro">Adding a second guardian to a child (e.g. a stepfather) needs two
      approvals: your Coordinator confirms it first, then a Gurukul Admin gives final approval.</p>
      <form id="gkAddGuardianForm" class="form-stack">
        <div class="form-group">
          <label for="gkGuardianLearner">Which learner?</label>
          <select id="gkGuardianLearner" required></select>
        </div>
        <div class="form-grid-row">
          <div class="form-group">
            <label for="gkGuardianFirstName">Their first name</label>
            <input type="text" id="gkGuardianFirstName" required>
          </div>
          <div class="form-group">
            <label for="gkGuardianLastName">Their last name</label>
            <input type="text" id="gkGuardianLastName" required>
          </div>
        </div>
        <div class="form-group">
          <label for="gkGuardianEmail">Their email</label>
          <input type="email" id="gkGuardianEmail" required>
        </div>
        <div class="form-group">
          <label for="gkGuardianRelationship">Relationship to the child</label>
          <select id="gkGuardianRelationship" required>
            <option value="">Select…</option>
            <option value="Parent">Parent</option>
            <option value="Legal Guardian">Legal Guardian</option>
          </select>
        </div>
        <div class="form-group">
          <label for="gkGuardianTempPassword">Temp password (used only if they're a brand-new account)</label>
          <input type="password" id="gkGuardianTempPassword" required minlength="8" maxlength="72" style="max-width:300px;">
        </div>
        <button type="submit" id="gkAddGuardianBtn">Submit for approval</button>
        <p id="gkAddGuardianResult" style="display:none;margin-top:8px;font-weight:600;"></p>
      </form>
    </div>
  `;
}

async function loadFamily() {
  const el = document.getElementById('gkFamilyList');
  const select = document.getElementById('gkGuardianLearner');
  if (!el) return;
  el.innerHTML = `<p class="empty-state">Loading…</p>`;
  try {
    const { learners } = await apiCall('/api/gurukul/learners');
    if (!learners.length) {
      el.innerHTML = `<p class="empty-state">No learners enrolled yet.</p>`;
      if (select) select.innerHTML = `<option value="">Enroll a learner first</option>`;
      return;
    }
    el.innerHTML = learners.map(l => `
      <div style="padding:8px 0;border-bottom:1px solid var(--border-color, #eee);">
        <strong>${escapeHtml(l.learner_id)}</strong> — ${escapeHtml(l.status)}
        <div style="font-size:0.85em;opacity:0.8;">
          Guardians: ${l.guardians.map(g => `${escapeHtml(g.name || g.email || g.guardian_id)} (${escapeHtml(g.relationship_label || '')}${g.is_primary ? ', primary' : ''})`).join('; ')}
        </div>
      </div>
    `).join('');
    if (select) {
      select.innerHTML = learners.map(l => `<option value="${l.id}">${escapeHtml(l.learner_id)}</option>`).join('');
    }
  } catch (err) {
    el.innerHTML = `<p class="empty-state">Failed to load: ${escapeHtml(err.message)}</p>`;
  }
}

function initMyFamilySection() {
  const enrollForm = document.getElementById('gkEnrollForm');
  if (enrollForm) {
    enrollForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const count = parseInt(document.getElementById('gkEnrollCount').value, 10) || 1;
      const relationship_label = document.getElementById('gkEnrollRelationship').value.trim();
      const btn = document.getElementById('gkEnrollBtn');
      const resultEl = document.getElementById('gkEnrollResult');
      btn.disabled = true;
      try {
        const { learnerIds } = await apiCall('/api/gurukul/enroll-learners', {
          method: 'POST', body: JSON.stringify({ count, relationship_label }),
        });
        const message = `✅ Enrolled: ${learnerIds.join(', ')}`;
        showToast(message, 'success');
        if (resultEl) { resultEl.textContent = message; resultEl.style.display = 'block'; }
        enrollForm.reset();
        document.getElementById('gkEnrollCount').value = 1;
        await loadFamily();
      } catch (err) {
        showToast(err.message || 'Enroll failed', 'error');
      } finally {
        btn.disabled = false;
      }
    });
  }

  const guardianForm = document.getElementById('gkAddGuardianForm');
  if (guardianForm) {
    guardianForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const payload = {
        target_learner_id: document.getElementById('gkGuardianLearner').value,
        first_name: document.getElementById('gkGuardianFirstName').value.trim(),
        last_name: document.getElementById('gkGuardianLastName').value.trim(),
        email: document.getElementById('gkGuardianEmail').value.trim(),
        relationship_label: document.getElementById('gkGuardianRelationship').value.trim(),
        temp_password: document.getElementById('gkGuardianTempPassword').value,
      };
      if (!payload.target_learner_id) {
        showToast('Select a learner', 'error');
        return;
      }
      const btn = document.getElementById('gkAddGuardianBtn');
      const resultEl = document.getElementById('gkAddGuardianResult');
      btn.disabled = true;
      try {
        await apiCall('/api/gurukul/guardian-requests', { method: 'POST', body: JSON.stringify(payload) });
        const message = `✅ Submitted. Your Coordinator will review it first, then a Gurukul Admin.`;
        showToast(message, 'success');
        if (resultEl) { resultEl.textContent = message; resultEl.style.display = 'block'; }
        guardianForm.reset();
      } catch (err) {
        showToast(err.message || 'Submit failed', 'error');
      } finally {
        btn.disabled = false;
      }
    });
  }

  loadFamily();
}

// ──────────────────────────── Approval Roles (Gurukul manager) ──────────────

function approvalRolesSectionHtml() {
  return `
    <div class="card" id="gk-sec-approval-roles">
      <h2>Approval Roles</h2>
      <p class="page-intro">Assign the department head (OPD), who gives final approval on
      add-guardian requests. Coordinators need no assignment — a team's lead (OPL) approves the
      first step automatically.</p>
      <form id="gkApprovalRoleForm" class="form-stack">
        <div class="form-group">
          <label for="gkApprovalPerson">Person</label>
          <select id="gkApprovalPerson" required></select>
        </div>
        <p style="margin:0 0 8px;">Role: <strong>OPD</strong> — Gurukul department head, final approver for everything</p>
        <button type="submit" id="gkApprovalRoleBtn">Assign</button>
        <p id="gkApprovalRoleResult" style="display:none;margin-top:8px;font-weight:600;"></p>
      </form>

      <h3 style="margin-top:16px;">Currently assigned</h3>
      <div class="table-container">
        <table class="table-stack-mobile">
          <thead><tr><th>Person</th><th>Role</th><th>Rank</th><th>Team</th><th></th></tr></thead>
          <tbody id="gkApprovalRolesBody"><tr><td colspan="5" class="empty-state">Loading…</td></tr></tbody>
        </table>
      </div>
    </div>
  `;
}

async function loadApprovalRoles() {
  const personSelect = document.getElementById('gkApprovalPerson');
  const teamSelect = document.getElementById('gkApprovalTeam');
  const tbody = document.getElementById('gkApprovalRolesBody');
  if (!tbody) return;
  tbody.innerHTML = `<tr><td colspan="5" class="empty-state">Loading…</td></tr>`;
  try {
    const { people, teams, assignments } = await apiCall('/api/gurukul/approval-roles');

    if (personSelect) {
      personSelect.innerHTML = people.length
        ? people.map(p => `<option value="${p.id}">${escapeHtml(p.name || p.email)}</option>`).join('')
        : `<option value="">No Gurukul users yet</option>`;
    }
    if (teamSelect) {
      teamSelect.innerHTML = teams.length
        ? teams.map(t => `<option value="${t.id}">${escapeHtml(t.name)}</option>`).join('')
        : `<option value="">No teams yet</option>`;
    }

    if (!assignments.length) {
      tbody.innerHTML = `<tr><td colspan="5" class="empty-state">No approval roles assigned yet.</td></tr>`;
      return;
    }
    tbody.innerHTML = assignments.map(a => `
      <tr>
        <td data-label="Person">${escapeHtml(a.name || a.email)}</td>
        <td data-label="Role">${escapeHtml(a.role_code)}</td>
        <td data-label="Rank">${a.rank === 1 ? 'Primary' : 'Secondary'}</td>
        <td data-label="Team">${escapeHtml(a.team_name || 'All teams')}</td>
        <td data-label="" class="action-buttons">${a.rank > 1 ? `<button type="button" class="gk-approval-primary-btn" data-id="${a.id}">Make primary</button> ` : ''}<button type="button" class="gk-approval-remove-btn" data-id="${a.id}">Remove</button></td>
      </tr>
    `).join('');

    tbody.querySelectorAll('.gk-approval-primary-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          await apiCall('/api/gurukul/approval-roles', { method: 'POST', body: JSON.stringify({ action: 'set_primary', id: btn.dataset.id }) });
          showToast('Primary changed', 'success');
          await loadApprovalRoles();
        } catch (err) {
          showToast(err.message || 'Failed', 'error');
          btn.disabled = false;
        }
      });
    });
    tbody.querySelectorAll('.gk-approval-remove-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          await apiCall('/api/gurukul/approval-roles-remove', { method: 'POST', body: JSON.stringify({ id: btn.dataset.id }) });
          showToast('Removed', 'success');
          await loadApprovalRoles();
        } catch (err) {
          showToast(err.message || 'Remove failed', 'error');
          btn.disabled = false;
        }
      });
    });
  } catch (err) {
    tbody.innerHTML = `<tr><td colspan="5" class="empty-state">Failed to load: ${escapeHtml(err.message)}</td></tr>`;
  }
}

function initApprovalRolesSection() {
  const form = document.getElementById('gkApprovalRoleForm');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const roleCode = 'OPD';
      const payload = {
        user_id: document.getElementById('gkApprovalPerson').value,
        role_code: roleCode,
      };
      if (!payload.user_id) {
        showToast('Select a person', 'error');
        return;
      }
      const btn = document.getElementById('gkApprovalRoleBtn');
      const resultEl = document.getElementById('gkApprovalRoleResult');
      btn.disabled = true;
      try {
        await apiCall('/api/gurukul/approval-roles', { method: 'POST', body: JSON.stringify(payload) });
        const message = `✅ Assigned ${roleCode}`;
        showToast(message, 'success');
        if (resultEl) { resultEl.textContent = message; resultEl.style.display = 'block'; }
        await loadApprovalRoles();
      } catch (err) {
        showToast(err.message || 'Assign failed', 'error');
      } finally {
        btn.disabled = false;
      }
    });
  }

  loadApprovalRoles();
}

// ──────────────────────────── Reset a password (admin / coordinator) ─────────
// Admin: any person with a Gurukul role (not platform system admins). Coordinator: people they added themselves.
// The admin types the temporary password; the person must change it at their next login.
function resetPwSectionHtml() {
  return `
    <div class="card gk-compact" id="gk-sec-reset-pw">
      <h2>Reset a password</h2>
      <p class="page-intro">For someone who cannot log in. Type a temporary password and tell it to them directly (not in a group chat). They must change it at their next login.</p>
      <div class="form-group"><label for="gkRpQ">Find the person (name or email)</label><input type="text" id="gkRpQ" maxlength="80" autocomplete="off" placeholder="at least 2 letters"></div>
      <div id="gkRpList"></div>
      <div id="gkRpForm" style="display:none;">
        <p style="margin:6px 0;">Resetting: <strong id="gkRpWho"></strong></p>
        <div class="form-grid-row">
          <div class="form-group"><label for="gkRpPw">Temporary password (8–72 characters)</label>
            <div class="gk-inline"><input type="password" id="gkRpPw" minlength="8" maxlength="72" autocomplete="new-password"><button type="button" id="gkRpShow" class="secondary">Show</button></div></div>
          <div class="form-group"><label for="gkRpPw2">Confirm</label><input type="password" id="gkRpPw2" minlength="8" maxlength="72" autocomplete="new-password"></div>
        </div>
        <button type="button" id="gkRpBtn">Reset password</button>
      </div>
    </div>`;
}

function initResetPwSection() {
  const q = document.getElementById('gkRpQ');
  if (!q) return;
  let chosen = null, t = null;
  const list = document.getElementById('gkRpList');
  const search = async () => {
    const term = q.value.trim();
    if (term.length < 2) { list.innerHTML = ''; return; }
    try {
      const { people } = await apiCall('/api/gurukul/reset-password?q=' + encodeURIComponent(term));
      list.innerHTML = people.length ? people.map(p => `<button type="button" class="secondary gk-rp-pick" data-id="${p.id}" data-label="${escapeHtml((p.name || '') + ' <' + p.email + '>')}" style="display:block;margin:4px 0;text-align:left;">${escapeHtml(p.name || '')} &lt;${escapeHtml(p.email)}&gt;</button>`).join('')
        : '<p class="page-intro">Nobody found that you can reset.</p>';
      list.querySelectorAll('.gk-rp-pick').forEach(b => b.addEventListener('click', () => {
        chosen = { id: b.dataset.id, label: b.dataset.label };
        document.getElementById('gkRpWho').textContent = chosen.label;
        document.getElementById('gkRpForm').style.display = '';
        list.innerHTML = '';
      }));
    } catch (err) { list.innerHTML = `<p class="page-intro">${escapeHtml(err.message)}</p>`; }
  };
  q.addEventListener('input', () => { clearTimeout(t); t = setTimeout(search, 300); });
  document.getElementById('gkRpShow').addEventListener('click', e => {
    const i = document.getElementById('gkRpPw'); const show = i.type === 'password';
    i.type = show ? 'text' : 'password'; e.target.textContent = show ? 'Hide' : 'Show';
  });
  document.getElementById('gkRpBtn').addEventListener('click', async () => {
    const pw = document.getElementById('gkRpPw').value, pw2 = document.getElementById('gkRpPw2').value;
    if (!chosen) { showToast('Choose the person first', 'error'); return; }
    if (pw.length < 8 || pw.length > 72) { showToast('The temporary password must be 8 to 72 characters', 'error'); return; }
    if (pw !== pw2) { showToast('The two passwords do not match', 'error'); return; }
    if (!(await showConfirm(`Reset the password of <strong>${escapeHtml(chosen.label)}</strong>? Their current password stops working now.`))) return;
    try {
      await apiCall('/api/gurukul/reset-password', { method: 'POST', body: JSON.stringify({ userId: chosen.id, temp_password: pw }) });
      showToast('Password reset. They must change it at their next login.', 'success');
      ['gkRpPw', 'gkRpPw2', 'gkRpQ'].forEach(id => { document.getElementById(id).value = ''; });
      document.getElementById('gkRpForm').style.display = 'none';
      chosen = null;
    } catch (err) { showToast(err.message, 'error'); }
  });
}

// ──────────────────────────── Page composition ──────────────────────────────

export function getGurukulAdminPage() {
  const manager = isGurukulManager();
  const coordinator = !manager && isGurukulCoordinator();
  const parent = isGurukulParent();

  if (!manager && !coordinator && !parent) {
    return `
      <h1 class="page-title">Gurukul</h1>
      <p class="page-intro">Welcome. Your team and learning views are coming soon here.</p>
    `;
  }

  return `
    <h1 class="page-title">Gurukul</h1>
    <div id="gkTabBar" class="gk-tabbar"></div>
    ${(manager || coordinator) ? addUserSectionHtml(manager) : ''}
    ${parent ? myFamilySectionHtml() : ''}
    ${manager ? approvalRolesSectionHtml() : ''}
    ${manager ? teamsSectionHtml() : ''}
    ${(manager || coordinator) ? retireSectionHtml(manager) : ''}
    ${manager ? auditSectionHtml() : ''}
  `;
}

// ─────────────────────────────────── Tabs ───────────────────────────────────
// Each existing card keeps its markup and logic; tabs only decide which cards are visible.
const TAB_CARDS = {
  adduser: ['gk-sec-add-user'],
  family: ['gk-sec-family-enroll', 'gk-sec-family', 'gk-sec-family-guardian'],
  teams: ['gk-sec-teams'],
  learners: ['gk-sec-learners', 'gk-sec-retire', 'gk-sec-retired'],
  lms: ['gk-sec-lms-updates'],
  settings: ['gk-sec-approval-roles', 'gk-sec-auto-add'],
  audit: ['gk-sec-audit'],
};
const TAB_LABELS = { adduser: 'Add User', family: 'My Family', teams: 'Teams', learners: 'Learners', lms: 'LMS updates', settings: 'Settings', audit: 'Audit trail' };

function selectTab(tab) {
  document.querySelectorAll('#gkTabBar button').forEach(b => b.classList.toggle('active', b.dataset.tab === tab));
  for (const [t, ids] of Object.entries(TAB_CARDS)) {
    ids.forEach(id => { const el = document.getElementById(id); if (el) el.style.display = t === tab ? '' : 'none'; });
  }
  try { sessionStorage.setItem('gkTab', tab); } catch (e) { /* optional */ }
}

function showTabFor(cardId) {
  const tab = Object.keys(TAB_CARDS).find(t => TAB_CARDS[t].includes(cardId));
  if (tab && document.querySelector('#gkTabBar button[data-tab="' + tab + '"]')) selectTab(tab);
}

function setupTabs({ manager, coordinator, parent }) {
  const tabs = [];
  if (manager || coordinator) tabs.push('adduser');
  if (parent) tabs.push('family');
  if (manager) tabs.push('teams', 'learners', 'lms', 'settings', 'audit');
  else if (coordinator) tabs.push('learners');
  const bar = document.getElementById('gkTabBar');
  bar.innerHTML = `<style>
    .gk-tabbar { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:14px; }
    .gk-tabbar button { background:#eef1ef; color:#333; border:1px solid #ccd3cf; padding:8px 14px; border-radius:6px; cursor:pointer; }
    .gk-tabbar button.active { background:#2D6A4F; color:#fff; border-color:#2D6A4F; }
  </style>` + tabs.map(t => `<button type="button" data-tab="${t}">${TAB_LABELS[t]}</button>`).join('');
  bar.querySelectorAll('button').forEach(b => b.addEventListener('click', () => selectTab(b.dataset.tab)));
  let saved = null;
  try { saved = sessionStorage.getItem('gkTab'); } catch (e) { /* optional */ }
  selectTab(tabs.includes(saved) ? saved : tabs[0]);
}

// ── World integration: the same cards, shown one page at a time (the sidebar replaces the tab bar) ──
const PAGE_CARDS = {
  'add-user': ['gk-sec-add-user', 'gk-sec-reset-pw'],
  family: ['gk-sec-family-enroll', 'gk-sec-family', 'gk-sec-family-guardian'],
  teams: ['gk-sec-teams'],
  learners: ['gk-sec-learners', 'gk-sec-retire', 'gk-sec-retired'],
  'my-team': ['gk-sec-retire'],
  'lms-updates': ['gk-sec-lms-updates'],
  settings: ['gk-sec-approval-roles', 'gk-sec-auto-add'],
  audit: ['gk-sec-audit'],
};
export const GURUKUL_CARD_PAGES = new Set(Object.keys(PAGE_CARDS));

export function getGurukulCardsHtml() {
  const manager = isGurukulManager();
  const coordinator = !manager && isGurukulCoordinator();
  const parent = isGurukulParent();
  return `
    ${(manager || coordinator) ? addUserSectionHtml(manager) : ''}
    ${(manager || coordinator) ? resetPwSectionHtml() : ''}
    ${parent ? myFamilySectionHtml() : ''}
    ${manager ? approvalRolesSectionHtml() : ''}
    ${manager ? teamsSectionHtml() : ''}
    ${(manager || coordinator) ? retireSectionHtml(manager) : ''}
    ${manager ? auditSectionHtml() : ''}
  `;
}

export async function initGurukulCards(page) {
  const manager = isGurukulManager();
  const coordinator = !manager && isGurukulCoordinator();
  const parent = isGurukulParent();
  if (manager || coordinator) initAddUserSection();
  if (manager || coordinator) initResetPwSection();
  if (parent) initMyFamilySection();
  if (manager) initApprovalRolesSection();
  if (manager) initTeamsSection();
  if (manager || coordinator) initRetireSection(manager);
  if (manager) initAuditSection();
  const show = new Set(PAGE_CARDS[page] || []);
  document.querySelectorAll('[id^="gk-sec-"]').forEach(el => { el.style.display = show.has(el.id) ? '' : 'none'; });
}

export async function initGurukulAdminPage() {
  const manager = isGurukulManager();
  const coordinator = !manager && isGurukulCoordinator();
  const parent = isGurukulParent();
  if (!manager && !coordinator && !parent) return;

  if (manager || coordinator) initAddUserSection();
  if (parent) initMyFamilySection();
  if (manager) initApprovalRolesSection();
  if (manager) initTeamsSection();
  if (manager || coordinator) initRetireSection(manager);
  if (manager) initAuditSection();
  setupTabs({ manager, coordinator, parent });
  const hash = (window.location.hash || '').replace('#', '');
  if (['requests', 'add-user-requests', 'guardian-requests', 'retire-requests'].includes(hash)) {
    window.location.replace('/gurukul/requests');
    return;
  }
  if (hash) {
    showTabFor('gk-sec-' + hash);
    setTimeout(() => document.getElementById('gk-sec-' + hash)?.scrollIntoView({ behavior: 'smooth' }), 600);
  }
}
